import { ReactNode } from 'react';
import { Skeleton } from '@mui/material';
import '@/styles/components/overview/OverviewInfoCard.scss';
import { useTranslation } from 'react-i18next';
import { OverViewInfo } from '@/types/pages/dashboard';

interface OverviewInfoCardProps {
    info: OverViewInfo;
    isClickable?: boolean;
    title: string;
    icon: ReactNode;
    onClick?: () => void;
}

const OverviewInfoCard = ({ info, title, icon, isClickable, onClick }: OverviewInfoCardProps) => {
    const { t } = useTranslation('common');

    const { loading, hasData, ratioNum, online, max } = info;
    const num = Number(`${ratioNum[0]}.${ratioNum[1]}`);
    const cardStatus = num >= 90 ? 'good-status' : num >= 80 ? 'risk-status' : 'danger-status';
    const clickable = !loading && isClickable;

    return (
        <div
            className={`overview-card-wrapper ${loading ? 'is-loading' : 'is-loaded'}`}
            onClick={clickable ? onClick : undefined}
        >
            {/* Skeleton layer */}
            <div className="overview-card skeleton">
                <div className="overview-card-text">
                    <Skeleton variant="text" width={150} height={40} />
                    <Skeleton variant="text" width={80} height={25} />
                    <Skeleton variant="text" width={120} height={25} />
                </div>

                <div className="overview-card-icon">
                    <Skeleton variant="circular" width={'100%'} height={'100%'} />
                </div>
            </div>

            {/* Real content layer */}
            <div className={`overview-card ${cardStatus} ${clickable ? 'clickable' : ''}`}>
                <div className="overview-card-text">
                    {hasData ? (
                        <>
                            <div className="overview-card-value">
                                <span className="integer">{ratioNum[0]}</span>
                                <span className="decimal">.{ratioNum[1]}</span>
                                <span className="percentage">%</span>
                            </div>
                            {hasData && (
                                <div className="overview-card-raw-values">
                                    <span>
                                        {online} / {max}
                                    </span>
                                </div>
                            )}
                            <div className="overview-card-title">{title}</div>
                        </>
                    ) : (
                        <div>{t('No data')}</div>
                    )}
                </div>

                <div className="overview-card-icon">{icon}</div>
            </div>
        </div>
    );
};

export default OverviewInfoCard;
